// The Brain (HELM-UI-BRAIN): a 2D knowledge map of agent_memory. helm_api memory_map returns the caller's VISIBLE
// memories (visibility is enforced by the gateway), projected and clustered server-side; this page draws them,
// with zoom about the cursor, drag to pan, keyboard control, a scope filter, a cluster legend and a memory list
// that doubles as the keyboard / screen-reader path to every node. Selecting a node (map or list) opens its complete
// record (memory_detail) in the details panel; ?memory=<slug> links straight to that panel.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip, Empty, ErrorNote, Icon, PageHeader, Spinner } from '../app/ui';
import { useApi } from '../lib/useApi';
import { ageLabel } from '../lib/format';
import { Prose } from './Rich';
import {
  HOME, VIEW, centreOn, clientToView, clusterColor, fetchMemoryDetail, memoryWebUrl, panBy, scopeOptions, scopePayload,
  unitPx, world, zoomAt,
} from '../lib/brain';
import type { MemoryDetailState, MemoryMapResponse, MemoryNode, ViewState } from '../lib/brain';

const ZOOM_STEP = 1.25;
const PAN_STEP = 60;
const DRAG_SLOP = 4;   // px moved before a press becomes a pan instead of a click

const ago = (iso: string): string => { const a = ageLabel(iso); return a === 'just now' ? a : `${a} ago`; };

/** The selected node's full record. A new id shows "loading" at once (never the previous record); late answers for an
 *  earlier selection are dropped. */
function useMemoryDetail(id: string | null): { state: MemoryDetailState | null; retry: () => void } {
  const [got, setGot] = useState<{ id: string; state: MemoryDetailState } | null>(null);
  const seq = useRef(0);
  const load = useCallback(() => {
    const my = ++seq.current;
    if (!id) return;
    setGot({ id, state: { status: 'loading' } });
    void fetchMemoryDetail(id).then((state) => { if (my === seq.current) setGot({ id, state }); });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  return { state: id ? (got?.id === id ? got.state : { status: 'loading' }) : null, retry: load };
}

/** The record part of the details panel: loading / denied / missing / error (retry) / the memory itself. */
export function MemoryDetailBody({ state, onRetry, onRefreshMap }: {
  state: MemoryDetailState; onRetry: () => void; onRefreshMap: () => void;
}) {
  const busy = state.status === 'loading';
  return (
    <div className="brain-record" aria-live="polite" aria-busy={busy}>
      {busy ? <Spinner label="Loading memory…" /> : null}
      {state.status === 'denied' ? (
        <div className="error-note" role="alert">
          Helm may not read this memory for you (agent_memory_read). Ask an admin to grant Helm access to its project.
        </div>
      ) : null}
      {state.status === 'missing' ? (
        <div className="brain-note" role="status">
          <p className="small">This memory no longer exists, was archived or replaced, or is no longer visible to you.</p>
          <button className="btn small" onClick={onRefreshMap}>Refresh map</button>
        </div>
      ) : null}
      {state.status === 'error' ? (
        <>
          <ErrorNote text={`Could not load this memory: ${state.message}`} />
          <div className="row gap"><button className="btn small" onClick={onRetry}>Retry</button></div>
        </>
      ) : null}
      {state.status === 'ready' ? <MemoryRecordView m={state.memory} /> : null}
    </div>
  );
}

function MemoryRecordView({ m }: { m: Extract<MemoryDetailState, { status: 'ready' }>['memory'] }) {
  const web = memoryWebUrl(m.slug);
  return (
    <>
      {m.ownedByYou && m.visibility === 'private' ? <div className="small muted">Private — only you can read it.</div> : null}
      {/* Prose: memoised (no re-parse of a long body on every pan/zoom frame) and its code blocks' Copy buttons work. */}
      <div className="brain-body"><Prose markdown={m.body} /></div>
      {m.bodyTruncated ? <div className="small muted">The text is long; Helm shows the first part.</div> : null}
      {m.tags.length ? <div className="small muted">Tags: {m.tags.join(', ')}</div> : null}
      {m.links.length ? <div className="small muted">Links to: <span className="mono">{m.links.join(', ')}</span></div> : null}
      <div className="small muted">
        {m.createdAt ? <>Created {ago(m.createdAt)}</> : null}
        {m.createdAt && m.updatedAt ? ' · ' : null}
        {m.updatedAt ? <>updated {ago(m.updatedAt)}</> : null}
        {m.source ? <> · via {m.source}</> : null}
      </div>
      {web ? <a className="btn small" href={web} target="_blank" rel="noopener noreferrer">Open in agent_memory</a> : null}
    </>
  );
}

export function BrainPage() {
  const [params, setParams] = useSearchParams();
  const scope = params.get('scope') ?? (params.get('project') ? `h:${params.get('project')}` : '');
  const payload = useMemo(() => scopePayload(scope), [scope]);
  const q = useApi<MemoryMapResponse>('memory_map', payload);
  const data = q.data;
  const nodes = data?.nodes ?? [];

  const [view, setView] = useState<ViewState>(HOME);
  // The selection lives in the URL (?memory=<slug>) so a memory's details panel can be linked to and survives reload.
  const selectedKey = params.get('memory') || null;
  const setSelectedKey = (key: string | null) => {
    if (key === selectedKey) return;
    const next = new URLSearchParams(params);
    if (key) next.set('memory', key); else next.delete('memory');
    setParams(next, { replace: true });
  };
  const [cluster, setCluster] = useState<string | null>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  useEffect(() => { setView(HOME); setCluster(null); }, [scope]);

  const byKey = useMemo(() => new Map(nodes.map((n) => [n.key, n])), [nodes]);
  const selected = selectedKey ? byKey.get(selectedKey) ?? null : null;
  const detail = useMemoryDetail(selected?.id ?? null);
  const refresh = () => { void q.reload(); if (selected) detail.retry(); };
  const clusters = data?.clusters ?? [];
  const clusterById = useMemo(() => new Map(clusters.map((c) => [c.id, c])), [clusters]);
  const options = useMemo(() => scopeOptions(data?.projects ?? []), [data?.projects]);
  const listed = useMemo(
    () => nodes.filter((n) => !cluster || n.clusterId === cluster).sort((a, b) => a.clusterId.localeCompare(b.clusterId, undefined, { numeric: true }) || a.title.localeCompare(b.title, undefined, { numeric: true })),
    [nodes, cluster],
  );

  const setScope = (v: string) => {
    const next = new URLSearchParams(params);
    next.delete('project');
    next.delete('memory');
    if (v) next.set('scope', v); else next.delete('scope');
    setParams(next, { replace: true });
  };

  // ── pointer: wheel zoom about the cursor (native listener: React's onWheel is passive), drag to pan ──
  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const [px, py] = clientToView(e.clientX, e.clientY, el.getBoundingClientRect());
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
      setView((v) => zoomAt(v, factor, px, py));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [nodes.length > 0]);

  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const [panning, setPanning] = useState(false);
  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
  };
  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
    if (!d.moved) { d.moved = true; setPanning(true); e.currentTarget.setPointerCapture(e.pointerId); }
    const s = unitPx(e.currentTarget.getBoundingClientRect());
    setView((v) => panBy(v, dx / s, dy / s));
    d.x = e.clientX;
    d.y = e.clientY;
  };
  const endDrag = (e: ReactPointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    drag.current = null;
    setPanning(false);
    if (d?.moved && e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    // A press that never moved is a click: on a node selects it, on empty space clears.
    if (d && !d.moved && e.type === 'pointerup') {
      const key = (e.target as Element).closest?.('[data-key]')?.getAttribute('data-key') ?? null;
      setSelectedKey(key);
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<SVGSVGElement>) => {
    const k = e.key;
    if (k === '+' || k === '=') setView((v) => zoomAt(v, ZOOM_STEP));
    else if (k === '-' || k === '_') setView((v) => zoomAt(v, 1 / ZOOM_STEP));
    else if (k === '0') setView(HOME);
    else if (k === 'ArrowLeft') setView((v) => panBy(v, PAN_STEP, 0));
    else if (k === 'ArrowRight') setView((v) => panBy(v, -PAN_STEP, 0));
    else if (k === 'ArrowUp') setView((v) => panBy(v, 0, PAN_STEP));
    else if (k === 'ArrowDown') setView((v) => panBy(v, 0, -PAN_STEP));
    else if (k === 'Escape') { setSelectedKey(null); setCluster(null); }
    else return;
    e.preventDefault();
  };

  const select = (n: MemoryNode, centre = false) => {
    setSelectedKey(n.key);
    if (centre) { const [wx, wy] = world(n); setView((v) => centreOn(v, wx, wy)); }
  };

  const hovered = hoverKey ? byKey.get(hoverKey) ?? null : null;
  const r = 6 / view.k;   // marks keep their on-screen size at any zoom
  const meta = data?.meta;

  return (
    <section className="page brain-page">
      <PageHeader title="Brain">
        <label className="sr-only" htmlFor="brain-scope">Scope</label>
        <select id="brain-scope" value={scope} onChange={(e) => setScope(e.target.value)} disabled={!data && q.loading}>
          <option value="">All projects</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label} ({o.memories})</option>)}
          {scope && !options.some((o) => o.value === scope) ? <option value={scope}>{data?.scope.project?.name ?? scope.slice(2)}</option> : null}
        </select>
        <button className="icon-btn" onClick={refresh} aria-label="Refresh" title="Refresh"><Icon name="refresh" /></button>
      </PageHeader>

      <p className="muted small">
        Knowledge map of agent memory — memories with similar meaning sit close together. You only see memories you may
        read (shared, your apps, your own private ones).
      </p>

      <ErrorNote text={q.error} />
      {q.loading && !data ? <Spinner label="Mapping memory…" /> : null}

      {data && selectedKey && !selected && !q.loading ? (
        <div className="brain-note row gap" role="status">
          <span className="small grow">The linked memory is not on this map — it may have been removed, be outside this scope, or not be visible to you.</span>
          <button className="btn small" onClick={() => setSelectedKey(null)}>Dismiss</button>
        </div>
      ) : null}

      {data && !nodes.length ? (
        <Empty>
          {data.scope.project && !data.scope.memoryProjects?.length
            ? <>No memories are tagged for {data.scope.project.name} (memory project “{data.scope.project.slug}”).</>
            : meta && meta.total > 0
              ? <>{meta.total} memories in scope, but none has an embedding yet — the map needs them (agent_memory backfill-embeddings).</>
              : <>No memories in this scope.</>}
        </Empty>
      ) : null}

      {data && nodes.length ? (
        <>
          <div className="row wrap gap small muted" aria-live="polite">
            <span>{meta!.returned} of {meta!.embedded} mapped memories{meta!.truncated ? ` (newest ${meta!.limit})` : ''}</span>
            {meta!.withoutEmbedding ? <span>· {meta!.withoutEmbedding} without an embedding</span> : null}
            <span>· {clusters.length} clusters</span>
            {meta!.explainedVariance ? <span title="Share of the variation the two axes show (PCA)">· axes explain {Math.round((meta!.explainedVariance[0] + meta!.explainedVariance[1]) * 100)}%</span> : null}
            <span className="spacer" />
            <div className="row gap" role="group" aria-label="Zoom">
              <button className="btn small" onClick={() => setView((v) => zoomAt(v, 1 / ZOOM_STEP))} aria-label="Zoom out">−</button>
              <span className="brain-zoom" aria-label={`Zoom ${view.k.toFixed(1)} times`}>{view.k.toFixed(1)}×</span>
              <button className="btn small" onClick={() => setView((v) => zoomAt(v, ZOOM_STEP))} aria-label="Zoom in">+</button>
              <button className="btn small" onClick={() => setView(HOME)}>Reset</button>
            </div>
          </div>

          <div className="row wrap gap brain-legend" role="group" aria-label="Clusters">
            {clusters.map((c) => (
              <button key={c.id} className={`chip brain-cluster${cluster === c.id ? ' on' : ''}`} aria-pressed={cluster === c.id}
                onClick={() => setCluster(cluster === c.id ? null : c.id)} title={c.projects.join(', ')}>
                <span className="brain-swatch" style={{ background: clusterColor(c.id) }} aria-hidden="true" />{c.label} · {c.size}
              </button>
            ))}
          </div>

          <div className="brain-layout">
            <div className="brain-canvas">
              <svg ref={svgRef} className={`brain-svg${panning ? ' panning' : ''}`} viewBox={`${-VIEW / 2} ${-VIEW / 2} ${VIEW} ${VIEW}`}
                tabIndex={0} role="img"
                aria-label={`Knowledge map: ${nodes.length} memories in ${clusters.length} clusters. Arrow keys pan, plus and minus zoom, 0 resets. Use the memory list to pick a memory.`}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
                onKeyDown={onKeyDown}>
                <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
                  {nodes.map((n) => {
                    const [x, y] = world(n);
                    const on = n.key === selectedKey;
                    const dim = cluster != null && n.clusterId !== cluster;
                    return (
                      <g key={n.key} data-key={n.key} className="brain-node" opacity={dim ? 0.15 : 1}
                        onPointerEnter={() => setHoverKey(n.key)} onPointerLeave={() => setHoverKey((h) => (h === n.key ? null : h))}>
                        <circle cx={x} cy={y} r={r * 2.2} fill="transparent" />{/* hit target bigger than the mark */}
                        <circle cx={x} cy={y} r={on ? r * 1.6 : r} fill={clusterColor(n.clusterId)}
                          stroke={on ? '#fff' : 'var(--surface)'} strokeWidth={on ? 2 : 2} vectorEffect="non-scaling-stroke" />
                      </g>
                    );
                  })}
                  {clusters.map((c) => {
                    const [x, y] = world(c);
                    const dim = cluster != null && c.id !== cluster;
                    return (
                      <text key={c.id} x={x} y={y - 14 / view.k} className="brain-label" fontSize={13 / view.k}
                        textAnchor="middle" opacity={dim ? 0.2 : 0.9} aria-hidden="true">{c.label}</text>
                    );
                  })}
                </g>
              </svg>
              {hovered && !panning ? (
                <div className="brain-tip" role="status">
                  <strong>{hovered.title}</strong>
                  {hovered.hook ? <div className="small muted">{hovered.hook}</div> : null}
                </div>
              ) : null}
            </div>

            <aside className="brain-side">
              {selected ? (
                <article className="card brain-detail" aria-label="Selected memory">
                  <div className="row gap top">
                    <h3 className="grow brain-title">{selected.title}</h3>
                    <button className="icon-btn" onClick={() => setSelectedKey(null)} aria-label="Close details"><Icon name="x" /></button>
                  </div>
                  {selected.hook ? <p className="small">{selected.hook}</p> : null}
                  <div className="row wrap gap">
                    <Chip tone="accent">{selected.type}</Chip>
                    {selected.project ? <Chip>{selected.project}</Chip> : null}
                    <Chip title="Who may read this memory">{selected.visibility}{selected.app ? `: ${selected.app}` : ''}</Chip>
                  </div>
                  <div className="small muted">
                    Cluster: <button className="link-btn" onClick={() => setCluster(selected.clusterId)}>{clusterById.get(selected.clusterId)?.label ?? selected.clusterId}</button>
                  </div>
                  <div className="small muted mono">{selected.slug}</div>
                  {detail.state ? <MemoryDetailBody state={detail.state} onRetry={detail.retry} onRefreshMap={refresh} /> : null}
                  <div className="row gap">
                    <button className="btn small" onClick={() => select(selected, true)}>Centre on map</button>
                  </div>
                </article>
              ) : null}

              <div className="card brain-list">
                <div className="row gap">
                  <strong className="grow">{cluster ? clusterById.get(cluster)?.label ?? 'Cluster' : 'Memories'}</strong>
                  <span className="badge">{listed.length}</span>
                  {cluster ? <button className="btn small" onClick={() => setCluster(null)}>Show all</button> : null}
                </div>
                <ul aria-label="Memories on the map">
                  {listed.map((n) => (
                    <li key={n.key}>
                      <button className={`brain-item${n.key === selectedKey ? ' on' : ''}`} aria-current={n.key === selectedKey ? 'true' : undefined}
                        onClick={() => select(n, true)}>
                        <span className="brain-swatch" style={{ background: clusterColor(n.clusterId) }} aria-hidden="true" />
                        <span className="grow">{n.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </>
      ) : null}
    </section>
  );
}
